import React from 'react'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { chainPropTypes } from '@mui/utils'
import { InputBase, MenuItem, Select, TableCell, Toolbar } from '@mui/material'
import Typography from '../Typography/Typography'
import PaginationActions from './PaginationActions'

const PREFIX = 'Pagination'

const classes = {
  paginationRoot: `${PREFIX}-paginationRoot`,
  toolbar: `${PREFIX}-toolbar`,
  spacer: `${PREFIX}-spacer`,
  caption: `${PREFIX}-caption`,
  selectRoot: `${PREFIX}-selectRoot`,
  select: `${PREFIX}-select`,
  selectIcon: `${PREFIX}-selectIcon`,
  input: `${PREFIX}-input`,
  menuItem: `${PREFIX}-menuItem`,
  actions: `${PREFIX}-actions`,
}

const StyledToolBar = styled(Toolbar)({
  height: 36,
  minHeight: 36,
  paddingRight: 2,
  '& .spacer': {
    flex: '1 1 100%',
  },
})

const StyledInputBase = styled(InputBase)({
  [`&.${classes.input}`]: {
    color: 'inherit',
    fontSize: 'inherit',
    flexShrink: 0,
    marginRight: 32,
    marginLeft: 8,
  },
})

const StyledSelect = styled(Select)({
  [`& .${classes.select}`]: {
    paddingLeft: 8,
    paddingRight: 24,
    textAlign: 'right',
    textAlignLast: 'right', // Align <select> on Chrome.
  },

  [`& .${classes.selectIcon}`]: {
    top: 1,
    right: -6,
  },
})

const defaultLabelDisplayedRows = ({ from, to, count }) =>
  `${from}-${to} of ${count}`
const defaultRowsPerPageOptions = [10, 25, 50, 100]

const getPages = ({ count, rowsPerPage }) => {
  const pages = []
  const totalPages = Math.ceil(count / rowsPerPage)

  for (let p = 1; p <= totalPages; p++) {
    pages.push(p)
  }
  return pages
}

/**
 * A `TableCell` based component for placing inside `TableFooter` for pagination.
 */
const Pagination = props => {
  const {
    ActionsComponent = PaginationActions,
    backIconButtonProps,
    colSpan: colSpanProp,
    component: Component = TableCell,
    count,
    labelDisplayedRows = defaultLabelDisplayedRows,
    labelRowsPerPage = props.paginationLabel
      ? props.paginationLabel
      : 'Rows per page:',
    nextIconButtonProps,
    onChangePage,
    onChangeRowsPerPage,
    page,
    rowsPerPage,
    rowsPerPageOptions = defaultRowsPerPageOptions,
    SelectProps = {},
    labelPage = 'Page:',
    ...other
  } = props

  let colSpan
  const pages = getPages(props)

  if (Component === TableCell || Component === 'td') {
    colSpan = colSpanProp || 1000 // col-span over everything
  }

  const MenuItemComponent = SelectProps.native ? 'option' : MenuItem

  const StyledComponent = styled('div')(({ theme }) => ({
    fontFamily: 'Roboto',
    color: '#666666',
    fontSize: theme.typography.pxToRem(12),
    // Increase the specificity to override TableCell.
    '&:last-child': {
      padding: 0,
    },
  }))

  const StyledActionsComponent = styled(ActionsComponent)({
    flexShrink: 0,
    marginLeft: 20,
  })

  return (
    <StyledComponent colSpan={colSpan} {...other}>
      <StyledToolBar>
        <div className="spacer" />
        {pages.length > 0 && (
          <Typography
            sx={{
              flexShrink: 0,
            }}
            color="inherit"
            variant="caption"
          >
            {labelPage}
          </Typography>
        )}
        {pages.length > 0 && (
          <StyledSelect
            classes={{
              select: classes.select,
              icon: classes.selectIcon,
            }}
            input={<StyledInputBase className={classes.input} />}
            value={page}
            onChange={event => {
              onChangePage(event, Number(event.target.value))
            }}
            {...SelectProps}
          >
            {pages.map(page => (
              <MenuItemComponent key={page} value={page}>
                {page}
              </MenuItemComponent>
            ))}
          </StyledSelect>
        )}
        {rowsPerPageOptions.length > 1 && (
          <Typography
            color="inherit"
            variant="caption"
            sx={{
              flexShrink: 0,
            }}
          >
            {labelRowsPerPage}
          </Typography>
        )}
        {rowsPerPageOptions.length > 1 && (
          <StyledSelect
            classes={{
              select: classes.select,
              icon: classes.selectIcon,
            }}
            input={<StyledInputBase className={classes.input} />}
            value={rowsPerPage}
            onChange={event => {
              onChangeRowsPerPage(event, Number(event.target.value))
            }}
            {...SelectProps}
          >
            {rowsPerPageOptions.map(rowsPerPageOption => (
              <MenuItemComponent
                key={rowsPerPageOption}
                value={rowsPerPageOption}
                className={classes.menuItem}
              >
                {rowsPerPageOption}
              </MenuItemComponent>
            ))}
          </StyledSelect>
        )}
        <Typography color="inherit" variant="caption" sx={{ flexShrink: 0 }}>
          {labelDisplayedRows({
            from: count === 0 ? 0 : (page - 1) * rowsPerPage + 1,
            to: Math.min(count, page * rowsPerPage),
            count,
            page,
          })}
        </Typography>
        <StyledActionsComponent
          backIconButtonProps={backIconButtonProps}
          count={count}
          nextIconButtonProps={nextIconButtonProps}
          page={page}
          rowsPerPage={rowsPerPage}
          onChangePage={onChangePage}
        />
      </StyledToolBar>
    </StyledComponent>
  )
}

Pagination.propTypes = {
  /**
   * The component used for displaying the actions.
   * Either a string to use a DOM element or a component.
   */
  ActionsComponent: PropTypes.elementType,
  /**
   * Properties applied to the back arrow [`IconButton`](/api/icon-button/) component.
   */
  backIconButtonProps: PropTypes.object,
  /**
   * Override or extend the styles applied to the component.
   * See [CSS API](#css) below for more details.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  colSpan: PropTypes.number,
  /**
   * The component used for the root node.
   * Either a string to use a DOM element or a component.
   */
  component: PropTypes.elementType,
  /**
   * The total number of rows.
   */
  count: PropTypes.number.isRequired,
  /**
   * Customize the displayed rows label.
   */
  labelDisplayedRows: PropTypes.func,
  /**
   * Customize the rows per page label. Invoked with a `{ from, to, count, page }`
   * object.
   */
  labelRowsPerPage: PropTypes.node,
  /**
   * Properties applied to the next arrow [`IconButton`](/api/icon-button/) element.
   */
  nextIconButtonProps: PropTypes.object,
  /**
   * Callback fired when the page is changed.
   *
   * @param {object} event The event source of the callback
   * @param {number} page The page selected
   */
  onChangePage: PropTypes.func.isRequired,
  /**
   * Callback fired when the number of rows per page is changed.
   *
   * @param {object} event The event source of the callback
   */
  onChangeRowsPerPage: PropTypes.func,
  /**
   * The non-zero based index of the current page.
   */
  page: chainPropTypes(PropTypes.number.isRequired, props => {
    const { count, page, rowsPerPage } = props
    const newLastPage = Math.max(1, Math.ceil(count / rowsPerPage))
    if (page < 1 || page > newLastPage) {
      return new Error(
        'Material-UI: the page prop of a Pagination is out of range ' +
          `(1 to ${newLastPage}, but page is ${page}).`,
      )
    }
    return null
  }),
  /**
   * The number of rows per page.
   */
  rowsPerPage: PropTypes.number.isRequired,
  /**
   * Customizes the options of the rows per page select field. If less than two options are
   * available, no select field will be displayed.
   */
  rowsPerPageOptions: PropTypes.array,
  /**
   * Properties applied to the rows per page [`Select`](/api/select/) element.
   */
  SelectProps: PropTypes.object,
}

export default Pagination
